import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { GraduationCap, Play, Clock, Users, Star, BookOpen, Award, TrendingUp } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const Courses = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [enrolledCourses, setEnrolledCourses] = useState([]);

  const categories = [
    { id: 'all', name: 'All Courses' },
    { id: 'clinical', name: 'Clinical Skills' },
    { id: 'management', name: 'Practice Management' },
    { id: 'technology', name: 'Technology' },
    { id: 'communication', name: 'Communication' }
  ];

  useEffect(() => {
    fetchCourses();
    // Load enrolled courses from localStorage
    const enrolled = JSON.parse(localStorage.getItem('enrolledCourses') || '[]');
    setEnrolledCourses(enrolled);
  }, []);

  const fetchCourses = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/courses`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      if (data.success) {
        setCourses(data.courses);
      }
    } catch (error) {
      console.error('Error fetching courses:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEnroll = async (courseId) => {
    if (!enrolledCourses.includes(courseId)) {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/courses/enroll`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({ course_id: courseId })
        });
        
        const data = await response.json();
        if (data.success) {
          const updated = [...enrolledCourses, courseId];
          setEnrolledCourses(updated);
          localStorage.setItem('enrolledCourses', JSON.stringify(updated));
          alert('Successfully enrolled in course!');
        } else {
          alert(data.message || 'Failed to enroll');
        }
      } catch (error) {
        console.error('Error enrolling:', error);
        alert('Error enrolling in course');
      }
    }
  };

  const isEnrolled = (courseId) => enrolledCourses.includes(courseId);

  // Mock courses data if none from backend
  const mockCourses = [
    {
      id: '1',
      title: 'Advanced Implant Techniques',
      description: 'Master advanced implant placement and restoration techniques',
      category: 'clinical',
      duration: '8 hours',
      instructor: 'Dr. William Gray',
      level: 'Advanced',
      enrolled: 156,
      rating: 4.8
    },
    {
      id: '2',
      title: 'Patient Communication Excellence',
      description: 'Learn effective communication strategies for better patient outcomes',
      category: 'communication',
      duration: '4 hours',
      instructor: 'Dr. Sarah Johnson',
      level: 'Intermediate',
      enrolled: 243,
      rating: 4.9
    },
    {
      id: '3',
      title: 'Digital Workflow Integration',
      description: 'Integrate digital tools into your practice workflow',
      category: 'technology',
      duration: '6 hours',
      instructor: 'Dr. Michael Chen',
      level: 'Beginner',
      enrolled: 187,
      rating: 4.7
    },
    {
      id: '4',
      title: 'Practice Management Fundamentals',
      description: 'Build a thriving oral surgery practice',
      category: 'management',
      duration: '10 hours',
      instructor: 'Dr. Gerard Nikodem',
      level: 'Beginner',
      enrolled: 312,
      rating: 4.9
    },
    {
      id: '5',
      title: 'Complex Bone Grafting Procedures',
      description: 'Advanced techniques for challenging bone graft cases',
      category: 'clinical',
      duration: '12 hours',
      instructor: 'Dr. David Schertzer',
      level: 'Advanced',
      enrolled: 98,
      rating: 4.8
    },
    {
      id: '6',
      title: 'CBCT Imaging Interpretation',
      description: 'Master 3D imaging analysis and treatment planning',
      category: 'technology',
      duration: '8 hours',
      instructor: 'Dr. Elizabeth Fassler',
      level: 'Intermediate',
      enrolled: 134,
      rating: 4.6
    }
  ];

  const displayCourses = courses.length > 0 ? courses : mockCourses;
  const filteredCourses = selectedCategory === 'all' 
    ? displayCourses 
    : displayCourses.filter(c => c.category === selectedCategory);

  const getLevelColor = (level) => {
    switch(level?.toLowerCase()) {
      case 'beginner': return 'bg-green-100 text-green-800 border-green-200';
      case 'intermediate': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'advanced': return 'bg-purple-100 text-purple-800 border-purple-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#D4AF37] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading courses...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-[#0A1628] mb-2 flex items-center">
          <GraduationCap className="h-8 w-8 mr-3 text-[#D4AF37]" />
          Learning Management System
        </h1>
        <p className="text-gray-600">Continue education and professional development</p>
      </div>

      {/* Stats */}
      <div className="grid md:grid-cols-4 gap-4 mb-8">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Courses</p>
                <p className="text-2xl font-bold text-[#0A1628]">{displayCourses.length}</p>
              </div>
              <BookOpen className="h-8 w-8 text-[#D4AF37]" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Enrolled</p>
                <p className="text-2xl font-bold text-[#0A1628]">{enrolledCourses.length}</p>
              </div>
              <Users className="h-8 w-8 text-[#D4AF37]" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Completed</p>
                <p className="text-2xl font-bold text-[#0A1628]">2</p>
              </div>
              <Award className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Avg Rating</p>
                <p className="text-2xl font-bold text-[#0A1628]">4.8</p>
              </div>
              <Star className="h-8 w-8 text-[#D4AF37]" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Category Filter */}
      <div className="flex space-x-2 mb-6 overflow-x-auto pb-2">
        {categories.map((category) => (
          <Button
            key={category.id}
            variant={selectedCategory === category.id ? "default" : "outline"}
            onClick={() => setSelectedCategory(category.id)}
            className={selectedCategory === category.id ? "bg-[#0A1628] text-[#D4AF37]" : ""}
          >
            {category.name}
          </Button>
        ))}
      </div>

      {/* Courses Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredCourses.map((course) => (
          <Card key={course.id} className="hover:shadow-lg transition-shadow">
            <div className="h-48 bg-gradient-to-br from-[#0A1628] to-[#1a2332] flex items-center justify-center">
              <Play className="h-16 w-16 text-[#D4AF37]" />
            </div>
            <CardHeader>
              <div className="flex items-start justify-between mb-2">
                <Badge className={getLevelColor(course.level)}>
                  {course.level}
                </Badge>
                <div className="flex items-center text-sm">
                  <Star className="h-4 w-4 text-yellow-400 fill-yellow-400 mr-1" />
                  <span className="font-semibold">{course.rating}</span>
                </div>
              </div>
              <CardTitle className="text-lg">{course.title}</CardTitle>
              <CardDescription>{course.description}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2 text-sm text-gray-600">
                <div className="flex items-center justify-between">
                  <span className="flex items-center">
                    <Clock className="h-4 w-4 mr-2" />
                    {course.duration}
                  </span>
                  <span className="flex items-center">
                    <Users className="h-4 w-4 mr-2" />
                    {course.enrolled} enrolled
                  </span>
                </div>
                <div className="flex items-center">
                  <GraduationCap className="h-4 w-4 mr-2" />
                  {course.instructor}
                </div>
              </div>
              <Button 
                className={isEnrolled(course.id) ? "w-full bg-green-600 text-white hover:bg-green-700" : "w-full bg-[#D4AF37] text-[#0A1628] hover:bg-[#C19B2B]"}
                onClick={() => {
                  if (isEnrolled(course.id)) {
                    navigate(`/courses/${course.id}/player`);
                  } else {
                    handleEnroll(course.id);
                  }
                }}
              >
                {isEnrolled(course.id) ? 'Start Course ▶' : 'Enroll Now'}
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default Courses;
